import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { lazy, Suspense, useEffect, useState, useRef } from 'react'
import HUD from '../components/HUD'
import RetroHUD from '../components/RetroHUD'
import TrainingHud from '../components/TrainingHud'
import VirtualGamepad from '../components/VirtualGamepad'
import LoadingArena from '../components/LoadingArena'
import { GameBus } from '@game/core/GameBus'
import { useAuth } from '../hooks/useAuth'
import { api } from '../api/client'
import stagesData from '@game/data/stages.json'
import type { Stage } from '@shared/types'

// PhaserHost (e toda a engine Phaser + cenas + entidades) só é baixado
// quando o usuário entra numa fase pela primeira vez. Login, menu,
// bestiário e configurações nunca carregam esse chunk. Como o GameBus
// usa sticky events ('stage:start' fica cacheado), os emits feitos
// antes do Phaser montar são entregues corretamente quando ele chega.
const PhaserHost = lazy(() => import('../components/PhaserHost'))

// Cenas onde a presença do jogador já justifica esconder o overlay
// de carregamento. PreloadScene/HubScene ainda estão carregando assets.
const GAMEPLAY_SCENES = new Set([
  'StageScene',
  'BossScene',
  'RhythmScene',
  'TrainingScene',
  'TutorialScene',
  'SurvivalScene',
  'EpilogueScene',
  'IntroScene',
  'BossRushScene',
  'BerimbauChallenge',
])

export default function GamePage() {
  const { stageId } = useParams<{ stageId: string }>()
  const [searchParams] = useSearchParams()
  const stageIndex = searchParams.get('index')
  const navigate = useNavigate()
  const { progress, setProgress } = useAuth()
  const lastStartedRef = useRef<string | null>(null)
  const gameplayStartedRef = useRef(false)

  // HUD condicional: combate normal vs treino. Reage ao scene:changed
  // emitido pela cena Phaser ao montar.
  const [currentScene, setCurrentScene] = useState<string>('')
  // Overlay de carregamento permanece visível até uma cena de gameplay
  // emitir scene:changed (significa que preload + create já rodaram).
  const [sceneReady, setSceneReady] = useState(false)

  useEffect(() => {
    const off = GameBus.on('scene:changed', ({ scene }) => {
      setCurrentScene(scene)
      if (GAMEPLAY_SCENES.has(scene)) {
        setSceneReady(true)
        gameplayStartedRef.current = true
      }
      if (scene === 'HubScene' && gameplayStartedRef.current) {
        navigate('/menu')
      }
    })

    // Trava em modo horizontal (Landscape) ao entrar no jogo
    ;(window as any).AndroidBridge?.lockLandscape?.()

    return off
  }, [navigate])

  // Fase zero: ao concluir OU abandonar o tutorial, grava a flag (não força
  // de novo) e navega — direto pra primeira fase, ou de volta ao menu.
  useEffect(() => {
    const off = GameBus.on('tutorial:done', ({ goTo }) => {
      try {
        localStorage.setItem('cs_tutorial_done', '1')
      } catch {
        /* ignore */
      }
      if (goTo === 'stage_01') navigate('/play/stage_01_quintal?index=0')
      else navigate('/menu')
    })
    return off
  }, [navigate])

  useEffect(() => {
    if (!stageId || !progress) return
    if (lastStartedRef.current === stageId) return
    lastStartedRef.current = stageId

    // Sticky: HubScene recebe esse evento mesmo se montar depois do emit.
    GameBus.emit('stage:start', {
      stageId,
      profile: progress,
      stageIndex: stageIndex ? parseInt(stageIndex) : undefined
    })

    // 10s entre fim da partida e volta pro mapa — permite a ResultScene
    // tocar a cascata de métricas e o jogador celebrar/digerir antes do
    // unmount do Phaser. O save no backend acontece imediatamente.
    const RETURN_TO_MENU_DELAY_MS = 10_000
    const pendingTimers: number[] = []
    const scheduleReturn = () => {
      const id = window.setTimeout(() => navigate('/menu'), RETURN_TO_MENU_DELAY_MS)
      pendingTimers.push(id)
    }

    const handleEnd = async (result: any) => {
      try {
        if (result.stageId === 'survival') {
          const res = await api.saveSurvivalRecord({
            waveReached: result.metrics?.waveReached || 0,
            score: result.score
          })
          if (progress) {
            setProgress({ ...progress, totalXp: res.totalXp })
          }
          scheduleReturn()
        } else {
          // Inclui o índice para desbloqueio linear no servidor
          const finalResult = {
            ...result,
            stageIndex: stageIndex ? parseInt(stageIndex) : undefined
          }
          const res = await api.saveProgress(finalResult)

          if (progress) {
            const updatedProgress = {
              ...progress,
              currentCorda: res.newCorda || progress.currentCorda,
              totalXp: res.totalXp,
              axe_charges: res.axe_charges ?? progress.axe_charges,
              maxStageIndex: res.maxStageIndex ?? progress.maxStageIndex
            }
            setProgress(updatedProgress)

            // Checagem de Final de Jogo (Epílogo): se for vitória na
            // ÚLTIMA fase de combate, dispara o epílogo no Phaser.
            const battleStages = (stagesData as Stage[]).filter(s => s.scene === 'StageScene')
            const isLastStage = stageIndex && parseInt(stageIndex) === battleStages.length - 1

            if (result.victory && isLastStage) {
              GameBus.emit('scene:request', {
                key: 'EpilogueScene',
                data: { skin: progress.skin || 'raiz' }
              })
            } else {
              scheduleReturn()
            }
          } else {
            scheduleReturn()
          }
        }
      } catch (err) {
        console.error('Falha ao salvar progresso:', err)
        scheduleReturn()
      }
    }
    const unsubEnd = GameBus.on('stage:ended', handleEnd)

    return () => {
      unsubEnd()
      pendingTimers.forEach((id) => window.clearTimeout(id))
    }
  }, [stageId, stageIndex, progress, navigate, setProgress])

  const isTraining = currentScene === 'TrainingScene'
  const isTutorial = currentScene === 'TutorialScene'
  const isBossRush = stageId === 'special_boss_rush' || currentScene === 'BossRushScene'
  const isBerimbau = currentScene === 'BerimbauChallenge'

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <Suspense fallback={<LoadingArena />}>
        <PhaserHost key={stageId} />
      </Suspense>
      {/* Overlay continua sobre o canvas Phaser até a cena de combate
          terminar preload+create, evitando o "flash preto" depois que
          o Suspense fecha mas antes do background pintar. */}
      {!sceneReady && <LoadingArena />}
      {isBerimbau || isTutorial ? null : isTraining ? <TrainingHud /> : isBossRush ? <RetroHUD /> : <HUD />}
      {!isBerimbau && <VirtualGamepad />}
    </div>
  )
}
